import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
  Alert,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import HomeIcon from "@mui/icons-material/Home";
import { downloadTickets } from "../api";
import { getBookingDetails, cancelBookingById } from "../api/bookings";
import { initiatePayment, checkPaymentPrice } from "../utils/paymentFlow";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../components/layout";
import { getCityDisplay } from "../utils/city";

const PaymentStatus = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const queryParams = new URLSearchParams(location.search);
  const bookingId = queryParams.get("booking_id");

  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [expired, setExpired] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [priceChangeDialog, setPriceChangeDialog] = useState({
    open: false,
    bookedPrice: null,
    currentPrice: null,
  });

  useEffect(() => {
    sessionStorage.removeItem("checkout_form");
  }, []);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails(bookingId);
    }
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId) return;

    const interval = setInterval(async () => {
      try {
        const updated = await getBookingDetails(bookingId);
        setBookingDetails(updated);

        if (
          ["paid", "failed", "refunded", "cancelled"].includes(
            updated.payment_status
          )
        ) {
          clearInterval(interval);
        }
      } catch (err) {
        // polling error — ignore
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [bookingId]);

  useEffect(() => {
    if (
      !bookingDetails?.expires_at ||
      bookingDetails.payment_status !== "pending"
    )
      return;

    const interval = setInterval(() => {
      const expiresAt = new Date(bookingDetails.expires_at);
      const now = new Date();
      const diff = Math.max(0, expiresAt - now);
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      setTimeLeft({ minutes, seconds });
      if (diff <= 0) {
        setExpired(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [bookingDetails]);

  const fetchBookingDetails = async (id) => {
    try {
      const response = await getBookingDetails(id);
      setBookingDetails(response);
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async (confirmNewPrice = false) => {
    setIsPaying(true);
    setActionError(null);
    try {
      await initiatePayment(bookingDetails.id, { confirmNewPrice });
    } catch (error) {
      setIsPaying(false);
      if (error?.response?.status === 409 && error.response.data?.price_changed) {
        setPriceChangeDialog({
          open: true,
          bookedPrice: error.response.data.booked_price,
          currentPrice: error.response.data.current_price,
        });
        return;
      }
      setActionError(t("paymentResult.createPaymentError"));
    }
  };

  const handleRetryPayment = async () => {
    try {
      const quote = await checkPaymentPrice(bookingDetails.id);
      if (quote.price_changed) {
        setPriceChangeDialog({
          open: true,
          bookedPrice: quote.booked_price,
          currentPrice: quote.current_price,
        });
        return;
      }
      await proceedToPayment(false);
    } catch (error) {
      setActionError(t("paymentResult.createPaymentError"));
    }
  };

  const handleConfirmNewPrice = async () => {
    const newPrice = priceChangeDialog.currentPrice;
    setPriceChangeDialog({ open: false, bookedPrice: null, currentPrice: null });
    setBookingDetails((prev) => ({
      ...prev,
      price: newPrice,
      current_price: newPrice,
      price_changed: false,
    }));
    await proceedToPayment(true);
  };

  const handleBookingCancelation = async () => {
    setActionError(null);
    try {
      await cancelBookingById(bookingId);
      setBookingDetails({
        ...bookingDetails,
        payment_status: "cancelled",
      });
    } catch (e) {
      setActionError(t("paymentResult.cancelError"));
    }
  };

  const handleDownloadTickets = () => downloadTickets(bookingId);

  // ——— Loading ———
  if (loading) {
    return (
      <PageContainer sx={{ py: 6 }}>
        <Stack alignItems="center" spacing={3} sx={{ minHeight: 280 }}>
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary">
            {t("paymentResult.loading")}
          </Typography>
        </Stack>
      </PageContainer>
    );
  }

  // ——— No booking ———
  if (!bookingDetails) {
    return (
      <PageContainer sx={{ py: 6 }}>
        <Card
          sx={{
            maxWidth: 480,
            mx: "auto",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <ErrorOutlineIcon
              sx={{ fontSize: 56, color: "error.main", mb: 2 }}
              aria-hidden
            />
            <Typography variant="h6" color="error" gutterBottom>
              {t("paymentResult.loadError")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t("paymentResult.loadErrorHint")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
            >
              {t("paymentResult.goHome")}
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const { from, to, passengers, price, payment_status } = bookingDetails;

  const parseBookingDt = (value) => {
    if (!value) return null;
    const parsed = dayjs(value, "YYYY-MM-DD HH:mm");
    if (parsed.isValid()) return parsed;
    const fallback = dayjs(value);
    return fallback.isValid() ? fallback : null;
  };
  const journeyEndAt =
    parseBookingDt(to?.arrival_datetime) ||
    parseBookingDt(to?.departure_datetime) ||
    parseBookingDt(bookingDetails.date);
  const now = dayjs();
  const isTripLocked =
    journeyEndAt &&
    journeyEndAt.isBefore(now) &&
    now.diff(journeyEndAt, "hour", true) >= 24;
  const actionsEnabled = !isTripLocked;

  const statusConfig = {
    paid: {
      icon: CheckCircleOutlineIcon,
      iconColor: "success.main",
      title: t("paymentResult.paidTitle"),
      subtitle: t("paymentResult.paidSubtitle"),
      chipLabel: t("paymentResult.paidChip"),
      chipColor: "success",
    },
    pending: {
      icon: ScheduleIcon,
      iconColor: "warning.main",
      title: expired ? t("paymentResult.expiredTitle") : t("paymentResult.pendingTitle"),
      subtitle: expired
        ? t("paymentResult.expiredSubtitle")
        : t("paymentResult.pendingSubtitle"),
      chipLabel: t("paymentResult.pendingChip"),
      chipColor: "warning",
    },
    not_paid: {
      icon: ScheduleIcon,
      iconColor: "warning.main",
      title: t("paymentResult.notPaidTitle"),
      subtitle: t("paymentResult.notPaidSubtitle"),
      chipLabel: t("paymentResult.notPaidChip"),
      chipColor: "warning",
    },
    processing: {
      icon: HourglassEmptyIcon,
      iconColor: "info.main",
      title: t("paymentResult.processingTitle"),
      subtitle: t("paymentResult.processingSubtitle"),
      chipLabel: t("paymentResult.processingChip"),
      chipColor: "info",
    },
    failed: {
      icon: ErrorOutlineIcon,
      iconColor: "error.main",
      title: t("paymentResult.failedTitle"),
      subtitle: t("paymentResult.failedSubtitle"),
      chipLabel: t("paymentResult.failedChip"),
      chipColor: "error",
    },
    refunded: {
      icon: AttachMoneyIcon,
      iconColor: "warning.main",
      title: t("paymentResult.refundedTitle"),
      subtitle: t("paymentResult.refundedSubtitle"),
      chipLabel: t("paymentResult.refundedChip"),
      chipColor: "warning",
    },
    cancelled: {
      icon: CancelIcon,
      iconColor: "text.secondary",
      title: t("paymentResult.cancelledTitle"),
      subtitle: t("paymentResult.cancelledSubtitle"),
      chipLabel: t("paymentResult.cancelledChip"),
      chipColor: "default",
    },
  };

  const config = statusConfig[payment_status] || {
    icon: ScheduleIcon,
    iconColor: "text.secondary",
    title: t("paymentResult.unknownTitle", { status: payment_status }),
    subtitle: t("paymentResult.unknownSubtitle"),
    chipLabel: payment_status,
    chipColor: "default",
  };

  const StatusIcon = config.icon;

  return (
    <PageContainer sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ maxWidth: 640, mx: "auto" }}>
        {actionError && (
          <Alert
            severity="error"
            onClose={() => setActionError(null)}
            sx={{ mb: 2 }}
          >
            {actionError}
          </Alert>
        )}

        {/* Блок статусу */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "action.hover",
                color: config.iconColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <StatusIcon sx={{ fontSize: 40 }} aria-hidden />
            </Box>
            <Chip
              label={config.chipLabel}
              color={config.chipColor}
              size="medium"
              sx={{ mb: 2 }}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {config.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {config.subtitle}
            </Typography>

            {/* Таймер для pending */}
            {payment_status === "pending" && !expired && timeLeft && (
              <Paper
                variant="outlined"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                  px: 2.5,
                  mb: 3,
                  borderRadius: 2,
                  borderColor: "warning.main",
                  bgcolor: "action.hover",
                }}
              >
                <ScheduleIcon color="warning" />
                <Typography
                  variant="h6"
                  component="span"
                  color="warning.dark"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {String(timeLeft.minutes).padStart(2, "0")}:
                  {String(timeLeft.seconds).padStart(2, "0")}
                </Typography>
              </Paper>
            )}

            {/* Кнопки дій за статусом */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              {payment_status === "pending" && !expired && actionsEnabled && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleRetryPayment}
                    disabled={isPaying}
                    fullWidth={isMobile}
                  >
                    {isPaying ? t("paymentResult.processing") : t("paymentResult.payNow")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleBookingCancelation}
                    fullWidth={isMobile}
                  >
                    {t("paymentResult.cancelBooking")}
                  </Button>
                </>
              )}
              {payment_status === "not_paid" && actionsEnabled && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleRetryPayment}
                    disabled={isPaying}
                    fullWidth={isMobile}
                  >
                    {isPaying ? t("paymentResult.processing") : t("paymentResult.payNow")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleBookingCancelation}
                    fullWidth={isMobile}
                  >
                    {t("paymentResult.cancelBooking")}
                  </Button>
                </>
              )}
              {payment_status === "failed" && actionsEnabled && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleRetryPayment}
                    disabled={isPaying}
                    fullWidth={isMobile}
                  >
                    {isPaying ? t("paymentResult.processing") : t("paymentResult.tryAgain")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleBookingCancelation}
                    fullWidth={isMobile}
                  >
                    {t("paymentResult.cancelBooking")}
                  </Button>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Деталі поїздки */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "action.hover",
            mb: 3,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t("paymentResult.tripDetails")}
            </Typography>

            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <CalendarTodayIcon sx={{ color: "primary.main", mt: 0.25 }} fontSize="small" />
                <Typography variant="body2">
                  <strong>{t("paymentResult.date")}</strong>{" "}
                  {dayjs(from?.departure_datetime).format("DD.MM.YYYY")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <DirectionsBusIcon sx={{ color: "primary.main", mt: 0.25 }} fontSize="small" />
                <Typography variant="body2">
                  <strong>{t("paymentResult.route")}</strong> {getCityDisplay(from?.city)} → {getCityDisplay(to?.city)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <LocationOnIcon sx={{ color: "primary.main", mt: 0.25 }} fontSize="small" />
                <Typography variant="body2">
                  <strong>{t("paymentResult.departure")}</strong> {getCityDisplay(from?.city)}, {from?.address}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <LocationOnIcon sx={{ color: "primary.main", mt: 0.25 }} fontSize="small" />
                <Typography variant="body2">
                  <strong>{t("paymentResult.arrival")}</strong> {getCityDisplay(to?.city)}, {to?.address}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
              {t("paymentResult.passengers")}
            </Typography>
            <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
              {passengers?.map((p, index) => (
                <Typography key={index} component="li" variant="body2">
                  {p.name} {p.surname}{" "}
                  <Chip
                    size="small"
                    label={p.type === "adult" ? t("paymentResult.adult") : t("paymentResult.child")}
                    sx={{ height: 20, fontSize: "0.75rem", ml: 0.5 }}
                  />
                </Typography>
              ))}
            </Stack>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <AttachMoneyIcon color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight={700} color="error.main">
                {t("paymentResult.price", { amount: t("units.uahAmount", { amount: price }) })}
              </Typography>
            </Box>
            {bookingDetails.show_price_notice && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t("paymentResult.priceNotice")}
              </Alert>
            )}
            {bookingDetails.price_changed && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                {t("paymentResult.newPrice", { amount: t("units.uahAmount", { amount: bookingDetails.current_price }) })}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Головні дії */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
        >
          {payment_status === "paid" && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTickets}
              fullWidth={isMobile}
            >
              {t("paymentResult.downloadTickets")}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/")}
            fullWidth={isMobile}
          >
            {t("paymentResult.goHome")}
          </Button>
        </Stack>
      </Box>

      <Dialog
        open={priceChangeDialog.open}
        onClose={() => setPriceChangeDialog({ open: false, bookedPrice: null, currentPrice: null })}
      >
        <DialogTitle>{t("paymentResult.priceChangedTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("paymentResult.priceChangedBody")}
          </DialogContentText>
          <Typography variant="body2">
            {t("paymentResult.bookedPrice", { amount: t("units.uahAmount", { amount: priceChangeDialog.bookedPrice }) })}
          </Typography>
          <Typography variant="body2" color="warning.main">
            {t("paymentResult.newPrice", { amount: t("units.uahAmount", { amount: priceChangeDialog.currentPrice }) })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceChangeDialog({ open: false, bookedPrice: null, currentPrice: null })}>
            {t("paymentResult.dismiss")}
          </Button>
          <Button onClick={handleConfirmNewPrice} color="success" variant="contained" disabled={isPaying}>
            {isPaying ? t("paymentResult.processing") : t("paymentResult.payNewPrice")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default PaymentStatus;
